'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { YearGroup } from '@utils/postUtils';
import Link from 'next/link';
import React from 'react';

type Props = {
  groups: YearGroup[];
};

export default function ArchiveSidebar({ groups }: Props) {
  const [expandedYears, setExpandedYears] = React.useState<Set<number>>(() => {
    if (groups.length > 0) {
      return new Set([groups[0].year]);
    }
    return new Set();
  });

  const toggleYear = (year: number) => {
    setExpandedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) {
        next.delete(year);
      } else {
        next.add(year);
      }
      return next;
    });
  };

  return (
    <Box
      component="aside"
      sx={{
        minWidth: { md: '220px', lg: '220px' },
        width: { md: '220px', lg: '220px' },
        display: { xs: 'none', md: 'block' },
        flexShrink: 0,
        position: 'sticky',
        top: 80,
        alignSelf: 'flex-start',
        maxHeight: 'calc(100vh - 100px)',
        overflowY: 'auto',
      }}
    >
      <Typography variant="h6" fontWeight={600} mb={2}>
        Archive
      </Typography>
      {groups.map(({ year, months }) => (
        <Box key={year} mb={1.5}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color="text.primary"
            sx={{ cursor: 'pointer', mb: 0.5, userSelect: 'none' }}
            onClick={() => toggleYear(year)}
          >
            {expandedYears.has(year) ? '▾' : '▸'} {year}
          </Typography>
          {expandedYears.has(year) &&
            months
              .flatMap((m) => m.posts)
              .sort((a, b) => b.date.valueOf() - a.date.valueOf())
              .map((post) => (
                <Typography
                  key={post.id}
                  component={Link}
                  href={`/post/${post.id}`}
                  variant="body2"
                  display="block"
                  color="text.secondary"
                  sx={{
                    textDecoration: 'none',
                    py: 0.5,
                    pl: 2,
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {post.title}
                </Typography>
              ))}
        </Box>
      ))}
    </Box>
  );
}
